import { BigNumber } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractionsPoolToken } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { PermitData } from './useApprove';
import { useToNumber } from './useConverter';
import { formatFloat } from '../utils/format';
import useGenericCall from './useGenericCall';
import { useOffchainPoolToken } from './useOffchain';


export default function useDeposit(approvalState: ButtonState, amount: BigNumber, invalidInput: boolean, permitData: PermitData): [ButtonState, () => Promise<void>] {
  const onchainInteractionsPoolToken = useOnchainInteractionsPoolToken();
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const val = useToNumber(amount);
  const symbol = useGenericCall(useOffchainPoolToken(), poolToken => poolToken.getSymbol());
  const summary = `Deposit ${formatFloat(val)} ${symbol}`;
  
  const depositState: ButtonState = useMemo(() => {
    if (invalidInput) return ButtonState.Disabled;
    if (approvalState != ButtonState.Done) return ButtonState.Disabled;
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [invalidInput, approvalState, pending, amount]);

  const deposit = useCallback(async (): Promise<void> => {
    if (depositState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractionsPoolToken.deposit(amount, permitData, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [depositState, onchainInteractionsPoolToken, addTransaction, amount, summary, permitData]);

  return [depositState, deposit];
}